import { StatusBadgeProps } from '@apollo/orbit';
import { AdmonitionVariant } from '@apollo/orbit/src/components/Admonition/Admonition.context';

import {
  CloudProvider,
  RouterStatus,
  ShardStatus,
} from 'src/lib/graphqlTypes/types';

type CloudRouterBadgeProps = {
  colorScheme: StatusBadgeProps['colorScheme'];
  text: string;
};
export const getTransitionStateTitle = (
  nextStatus?: RouterStatus,
  shardStatus?: ShardStatus,
  provider?: CloudProvider,
) => {
  if (provider === CloudProvider.FLY) {
    return 'Router migration required.';
  }

  switch (shardStatus) {
    case ShardStatus.DEPRECATED:
      return 'Router migration required.';
    default:
      break;
  }

  switch (nextStatus) {
    case RouterStatus.SLEEPING:
      return 'Router will sleep soon.';
    case RouterStatus.DELETED:
      return 'Router will be deleted.';
    default:
      break; // fall through
  }
};

export const getAction = (status?: RouterStatus, nextStatus?: RouterStatus) => {
  switch (nextStatus) {
    case RouterStatus.SLEEPING:
    case RouterStatus.DELETED:
      return 'Run a query';
    default: // fall through
  }

  switch (status) {
    case RouterStatus.DELETED:
      // return 'Recreate router';
      return null;
    case RouterStatus.SLEEPING:
    case RouterStatus.RUNNING:
      return 'Run a query';
    default:
  }
};

export const getTransitionStateTitleClassname = (
  nextStatus?: RouterStatus,
  shardStatus?: ShardStatus,
  provider?: CloudProvider,
) => {
  if (
    provider === CloudProvider.FLY ||
    shardStatus === ShardStatus.DEPRECATED
  ) {
    return 'text-error';
  }
  switch (nextStatus) {
    case RouterStatus.SLEEPING:
      return 'text-warning';
    case RouterStatus.DELETED:
      return 'text-error';
    default: // fall through
  }
};

export const getBody = (status?: RouterStatus, nextStatus?: RouterStatus) => {
  switch (nextStatus) {
    case RouterStatus.SLEEPING:
      return 'Your cloud router hasn’t received traffic for 7 days and is scheduled to go to sleep soon.';
    case RouterStatus.DELETED:
      return `Your router has gone into “sleep mode” due to inactivity for 30 or more days.`;
    default: // fall through
  }

  switch (status) {
    case RouterStatus.RUNNING:
      return 'Looking good! Your graph is operating as expected.';
    case RouterStatus.SLEEPING:
      return 'Your router has gone into “sleep mode” due to inactivity for 30 or more days.';
    case RouterStatus.DELETED:
      return 'Your router has been deleted due to inactivity.';
    default: // fall through
  }
};

export const getColorScheme = (
  status?: RouterStatus,
  nextStatus?: RouterStatus,
  shardStatus?: ShardStatus,
  provider?: CloudProvider,
) => {
  if (provider === CloudProvider.FLY) {
    return 'red';
  }

  switch (shardStatus) {
    case ShardStatus.DEPRECATED:
      return 'red';
    default:
      break;
  }

  switch (nextStatus) {
    case RouterStatus.SLEEPING:
      return 'yellow';
    case RouterStatus.DELETED:
      return 'red';
    default: // fall through
  }

  switch (status) {
    case RouterStatus.DELETED:
    case RouterStatus.DELETING:
    case RouterStatus.ROLLING_BACK:
      return 'red';
    case RouterStatus.SLEEPING:
      return 'yellow';
    case RouterStatus.UPDATING:
      return 'blue';
    case RouterStatus.CREATING:
    case RouterStatus.RUNNING:
    default:
      return 'green';
  }
};

export const getBadgeProps = (
  status: RouterStatus,
  nextStatus?: RouterStatus | null,
  shardStatus?: ShardStatus,
  provider?: CloudProvider,
): CloudRouterBadgeProps => {
  const migrationProps = {
    colorScheme: 'red' as CloudRouterBadgeProps['colorScheme'],
    text: 'At-risk',
  };

  if (provider === CloudProvider.FLY) {
    return migrationProps;
  }

  switch (shardStatus) {
    case ShardStatus.DEPRECATED:
      return migrationProps;
    default:
      break; // Do nothing fall through to satisfy linter
  }

  switch (nextStatus) {
    case RouterStatus.SLEEPING:
      return {
        colorScheme: 'yellow',
        text: status,
      };
    case RouterStatus.DELETED:
      return {
        colorScheme: 'red',
        text: nextStatus,
      };
    default: // fall through
  }

  switch (status) {
    case RouterStatus.DELETED:
    case RouterStatus.DELETING:
    case RouterStatus.ROLLING_BACK:
      return {
        colorScheme: 'red',
        text: status,
      };
    case RouterStatus.SLEEPING:
      return {
        colorScheme: 'yellow',
        text: status,
      };
    case RouterStatus.UPDATING:
      return {
        colorScheme: 'blue',
        text: status,
      };
    case RouterStatus.CREATING:
    case RouterStatus.RUNNING:
    default:
      return {
        colorScheme: 'green',
        text: status,
      };
  }
};

export const getAdmonitionProps = (
  status?: RouterStatus,
  shardStatus?: ShardStatus,
  provider?: CloudProvider,
) => {
  const migrationProps = {
    variant: 'error' as AdmonitionVariant,
    title: 'Router migration required',
    body: `Your cloud router will be deleted if you do not migrate by June 27, 2024.`,
  };

  if (provider === CloudProvider.FLY) {
    return migrationProps;
  }

  switch (shardStatus) {
    case ShardStatus.DEPRECATED:
      return migrationProps;
    default:
      break;
  }

  switch (status) {
    case RouterStatus.DELETED:
      return {
        variant: 'error' as AdmonitionVariant,
        title: 'Your cloud router has been deleted',
        body: 'Your cloud router has been deleted. To continue serving traffic, you’ll need to recreate your graph’s cloud router.',
      };
    case RouterStatus.SLEEPING:
      return {
        variant: 'warning' as AdmonitionVariant,
        title: 'Your cloud router has gone to sleep',
        body: 'Your router has gone into “sleep mode” due to inactivity for 7 or more days.',
      };
    default:
  }
};
